import { useEffect, useMemo, useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import api, { errorMessage } from '../../services/api';
import { useAuth } from '../../services/auth';
import { StatusBadge, fmtDateTime } from '../../components/ui';
import { MODE_LABELS, formatPhone, whatsappLink } from '../../brand';
import { PageNav, PageAlert, Modal, inputStyle, EVENT_LABELS, describe } from './common';

const cardStyle = { borderRadius: '12px', background: '#ffffff', border: '1px solid #dee2e6' };
const MODE_BADGE = { whatsapp: 'bg-success', qr_code: 'bg-warning text-dark', direct: 'bg-primary' };
const BOX_NAMES = ['Gauche', 'Centre', 'Droite'];

// La photo du ticket est servie avec le jeton de connexion : on la charge en blob.
function TicketPhoto({ url }) {
  const [src, setSrc] = useState(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let objectUrl = null;
    api.get(url, { responseType: 'blob' })
      .then((r) => { objectUrl = URL.createObjectURL(r.data); setSrc(objectUrl); })
      .catch(() => setFailed(true));
    return () => { if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [url]);
  if (failed) return <div className="text-muted small">Photo indisponible.</div>;
  if (!src) return <div className="spinner-border spinner-border-sm" style={{ color: '#1565c0' }}></div>;
  return (
    <>
      <a href={src} target="_blank" rel="noopener noreferrer">
        <img src={src} alt="Ticket de caisse" style={{ maxWidth: '100%', maxHeight: '400px', borderRadius: '8px', cursor: 'pointer' }} />
      </a>
      <div className="text-center mt-2"><small style={{ color: '#555' }}>Cliquez sur l'image pour l'agrandir</small></div>
    </>
  );
}

function RoundAudit({ roundId }) {
  const [r, setR] = useState(null);
  useEffect(() => { api.get(`/palet/admin/rounds/${roundId}`).then((x) => setR(x.data)).catch(() => {}); }, [roundId]);
  if (!r) return <div className="spinner-border spinner-border-sm" style={{ color: '#1565c0' }}></div>;
  return (
    <div className="mt-2 small">
      {r.boxes && (
        <table className="table table-sm mb-2">
          <thead><tr><th>Tirage</th><th>Case</th><th>Lot</th><th>État</th></tr></thead>
          <tbody>{r.boxes.map((b, i) => (
            <tr key={i}><td>{b.version}</td><td>{BOX_NAMES[b.index]}</td><td>{b.prize_label}</td>
              <td>{{ chosen: 'Choisie', released: 'Non choisie, rendue', reserved: 'Réservée' }[b.state]}</td></tr>
          ))}</tbody>
        </table>
      )}
      <ul className="list-unstyled mb-0">
        {r.events.map((e, i) => (
          <li key={i} className="mb-1"><span className="text-muted">{fmtDateTime(e.at)}</span> · <strong>{EVENT_LABELS[e.type] || e.type}</strong>
            {e.user && <span className="text-muted"> · {e.user}</span>}
            {e.payload && <div className="text-muted" style={{ fontSize: '11px' }}>{describe(e.payload)}</div>}</li>
        ))}
      </ul>
    </div>
  );
}

export default function Participants() {
  const { user } = useAuth();
  const [participations, setParticipations] = useState([]);
  const [pdvs, setPdvs] = useState([]);
  const [prizes, setPrizes] = useState([]);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [filters, setFilters] = useState({ city_id: '', pdv_id: '', prize_config_id: '', date_from: '', q: '' });
  const [selected, setSelected] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [showWhatsApp, setShowWhatsApp] = useState(false);
  const [whatsappMessage, setWhatsappMessage] = useState('');
  const [auditId, setAuditId] = useState(null);

  const loadParticipations = async () => {
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const { data } = await api.get('/palet/admin/participations', { params });
      setParticipations(data);
    } catch (error) {
      setMessage({ type: 'danger', text: errorMessage(error, 'Erreur lors du chargement des participants') });
    } finally {
      setLoading(false);
    }
  };
  // Rafraîchissement automatique (30 s) avec les filtres courants.
  const loadRef = useRef(loadParticipations);
  loadRef.current = loadParticipations;
  useEffect(() => {
    const t = setInterval(() => loadRef.current(), 30000);
    return () => clearInterval(t);
  }, []);
  useEffect(() => { loadParticipations(); }, [filters.city_id, filters.pdv_id, filters.prize_config_id, filters.date_from]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    api.get('/admin/cities').then((r) => setCities(r.data)).catch(() => {});
    api.get('/admin/pdv').then((r) => setPdvs(r.data)).catch(() => {});
    api.get('/palet/admin/prizes').then((r) => setPrizes(r.data)).catch(() => {});
  }, []);

  const openDetails = async (p) => {
    try {
      setSelected((await api.get(`/palet/admin/participations/${p.id}`)).data);
      setAuditId(null);
      setShowDetails(true);
    } catch {
      setMessage({ type: 'danger', text: 'Erreur lors du chargement des détails' });
    }
  };

  const openWhatsApp = (p) => {
    setSelected(p);
    setWhatsappMessage('');
    setShowWhatsApp(true);
  };

  const sendWhatsApp = () => {
    if (!whatsappMessage.trim()) { setMessage({ type: 'danger', text: 'Veuillez entrer un message' }); return; }
    window.open(whatsappLink(selected.participant_phone, whatsappMessage.trim()), '_blank');
    setMessage({ type: 'success', text: 'Lien WhatsApp ouvert !' });
    setShowWhatsApp(false);
  };

  const lotsText = (p) => p.plays_history.map((h) => `${p.plays_total > 1 ? `L${h.play_number}: ` : ''}${h.prize_label || (h.status === 'open' ? 'non joué' : h.status === 'cancelled' ? 'annulé' : '-')}`).join(' • ');

  const exportToExcel = () => {
    const rows = participations.map((p) => ({
      Date: new Date(p.created_at).toLocaleDateString('fr-FR'),
      Heure: new Date(p.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }),
      Participant: p.participant_name, Contact: p.participant_phone, Ville: p.city_name || '-', PDV: p.pdv_name,
      'Hôtesse': p.hotesse_name, Mode: MODE_LABELS[p.play_mode] || p.play_mode,
      Statut: p.cancelled ? 'Annulé' : p.played ? 'Joué' : 'En attente',
      Lancers: p.plays_total, Lots: lotsText(p),
      Codes: p.plays_history.map((h) => h.code).join(' '),
      'Remis': p.plays_history.filter((h) => h.delivered).length,
      Bouteilles: p.bottles_count, 'N° ticket': p.ticket_ref || '', 'Photo ticket': p.has_ticket_photo ? 'Oui' : 'Non',
    }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), 'Participants');
    XLSX.writeFile(wb, `participants_palet_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const visiblePdvs = useMemo(() => pdvs.filter((p) => !filters.city_id || String(p.city_id) === String(filters.city_id)), [pdvs, filters.city_id]);

  if (loading) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center" style={{ background: '#f4f6f9' }}>
        <div className="spinner-border" role="status" style={{ color: '#1565c0' }}></div>
      </div>
    );
  }

  const lab = { color: '#333', fontWeight: 600 };

  return (
    <div className="min-vh-100" style={{ background: '#f4f6f9' }}>
      <PageNav user={user} title="Participants" />
      <div className="container-fluid px-3 px-lg-4 py-4" style={{ maxWidth: '1400px' }}>
        <PageAlert message={message} onClose={() => setMessage({ type: '', text: '' })} />

        <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
          <div>
            <h2 className="fw-bold mb-1" style={{ color: '#1a1a1a' }}><i className="fas fa-users me-2" style={{ color: '#1565c0' }}></i>Gestion des Participants</h2>
            <p className="mb-0" style={{ color: '#555' }}>{participations.length} participant(s)</p>
          </div>
          <button className="btn btn-success fw-bold" onClick={exportToExcel} disabled={!participations.length}>
            <i className="fas fa-file-excel me-2"></i>Exporter Excel
          </button>
        </div>

        <div className="card border-0 shadow mb-4" style={cardStyle}>
          <div className="card-body p-3">
            <div className="row g-3 align-items-end">
              <div className="col-6 col-md-2">
                <label className="form-label small" style={lab}>Ville</label>
                <select className="form-select form-select-sm" value={filters.city_id} onChange={(e) => setFilters({ ...filters, city_id: e.target.value, pdv_id: '' })} style={inputStyle}>
                  <option value="">Toutes les villes</option>
                  {cities.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div className="col-6 col-md-2">
                <label className="form-label small" style={lab}>PDV</label>
                <select className="form-select form-select-sm" value={filters.pdv_id} onChange={(e) => setFilters({ ...filters, pdv_id: e.target.value })} style={inputStyle}>
                  <option value="">Tous</option>
                  {visiblePdvs.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
              <div className="col-6 col-md-2">
                <label className="form-label small" style={lab}>Lot</label>
                <select className="form-select form-select-sm" value={filters.prize_config_id} onChange={(e) => setFilters({ ...filters, prize_config_id: e.target.value })} style={inputStyle}>
                  <option value="">Tous les lots</option>
                  {prizes.map((p) => <option key={p.id} value={p.id}>{p.prize_label}</option>)}
                </select>
              </div>
              <div className="col-6 col-md-2">
                <label className="form-label small" style={lab}>Date début</label>
                <input type="date" className="form-control form-control-sm" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} style={inputStyle} />
              </div>
              <div className="col-9 col-md-3">
                <label className="form-label small" style={lab}>Recherche</label>
                <form className="input-group input-group-sm" onSubmit={(e) => { e.preventDefault(); loadParticipations(); }}>
                  <input className="form-control" placeholder="Nom, téléphone ou code" value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} style={inputStyle} />
                  <button className="btn btn-outline-primary" type="submit" aria-label="Rechercher"><i className="fas fa-search"></i></button>
                </form>
              </div>
            </div>
          </div>
        </div>

        <div className="card border-0 shadow" style={cardStyle}>
          <div className="card-body p-0">
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead>
                  <tr>{['Date/Heure', 'Participant', 'Contact', 'PDV', 'Hôtesse', 'Mode', 'Statut', 'Lot', 'Actions'].map((c) => (
                    <th key={c} className="p-3" style={{ color: '#000', fontWeight: 'bold', textTransform: 'uppercase', fontSize: '12px', letterSpacing: '0.5px' }}>{c}</th>
                  ))}</tr>
                </thead>
                <tbody>
                  {participations.length === 0 ? (
                    <tr><td colSpan="9" className="text-center py-5">
                      <i className="fas fa-users-slash fa-3x mb-3" style={{ opacity: 0.3, color: '#1565c0' }}></i>
                      <p className="mb-0" style={{ color: '#555' }}>Aucun participant trouvé</p>
                    </td></tr>
                  ) : participations.map((p, idx) => (
                    <tr key={p.id} className="clickable-row" style={{ backgroundColor: idx % 2 === 0 ? '#f8f9fa' : '#ffffff' }} onClick={() => openDetails(p)}>
                      <td className="p-3">
                        <div style={{ fontSize: '12px', color: '#555', fontWeight: 600 }}>{new Date(p.created_at).toLocaleDateString('fr-FR')}</div>
                        <div style={{ fontSize: '13px', color: '#1a1a1a', fontWeight: 600 }}>{new Date(p.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</div>
                      </td>
                      <td className="p-3 fw-semibold" style={{ color: '#1a1a1a' }}>
                        {p.participant_name || '—'}
                        <div className="text-muted fw-normal" style={{ fontSize: '12px' }}>{p.bottles_count} bouteille{p.bottles_count > 1 ? 's' : ''}</div>
                      </td>
                      <td className="p-3 font-monospace" style={{ color: '#1a1a1a', whiteSpace: 'nowrap' }}>{formatPhone(p.participant_phone)}</td>
                      <td className="p-3" style={{ color: '#555' }}>{p.pdv_name}</td>
                      <td className="p-3" style={{ color: '#555' }}>{p.hotesse_name}</td>
                      <td className="p-3"><span className={`badge ${MODE_BADGE[p.play_mode] || 'bg-primary'}`}>{MODE_LABELS[p.play_mode] || p.play_mode}</span></td>
                      <td className="p-3">
                        {p.cancelled ? <span className="badge bg-danger">Annulé</span>
                          : p.played ? <span className="badge bg-success"><i className="fas fa-check-circle me-1"></i>Joué</span>
                            : <span className="badge bg-secondary"><i className="fas fa-clock me-1"></i>En attente</span>}
                      </td>
                      <td className="p-3">
                        <div className="d-flex flex-column gap-1">
                          {p.plays_history.map((h) => (
                            <span key={h.id} className={`badge ${h.is_winner ? (h.delivered ? 'bg-success' : 'bg-info text-dark') : 'bg-light text-dark border'}`} style={{ fontSize: '11px' }}>
                              {p.plays_total > 1 && `Lancer ${h.play_number}: `}
                              {h.prize_label || (h.status === 'open' ? 'non joué' : h.status === 'cancelled' ? 'annulé' : '-')}
                              {h.delivered && <i className="fas fa-check ms-1"></i>}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="p-3" onClick={(e) => e.stopPropagation()} style={{ whiteSpace: 'nowrap' }}>
                        <button className="btn btn-sm btn-success me-1" title="Envoyer WhatsApp" onClick={() => openWhatsApp(p)}><i className="fab fa-whatsapp"></i></button>
                        <button className="btn btn-sm btn-info" title="Voir détails" onClick={() => openDetails(p)}><i className="fas fa-eye"></i></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {showWhatsApp && selected && (
        <Modal title="Envoyer message WhatsApp" icon="fab fa-whatsapp" onClose={() => setShowWhatsApp(false)}
          footer={<>
            <button type="button" className="btn btn-secondary" onClick={() => setShowWhatsApp(false)}>Annuler</button>
            <button type="button" className="btn btn-success" onClick={sendWhatsApp}><i className="fab fa-whatsapp me-2"></i>Envoyer</button>
          </>}>
          <div className="mb-3"><label className="form-label" style={{ color: '#333' }}>Participant</label><input className="form-control" value={selected.participant_name || ''} disabled style={inputStyle} /></div>
          <div className="mb-3"><label className="form-label" style={{ color: '#333' }}>Téléphone</label><input className="form-control" value={selected.participant_phone || ''} disabled style={inputStyle} /></div>
          <div className="mb-3">
            <label className="form-label" style={{ color: '#333' }}>Message</label>
            <textarea className="form-control" rows="4" value={whatsappMessage} onChange={(e) => setWhatsappMessage(e.target.value)} placeholder="Entrez votre message..." style={inputStyle}></textarea>
          </div>
        </Modal>
      )}

      {showDetails && selected && (
        <Modal title="Détails du participant" icon="fas fa-user" size="modal-lg" onClose={() => setShowDetails(false)}
          footer={<>
            <button type="button" className="btn btn-primary" onClick={() => { setShowDetails(false); setWhatsappMessage(''); setShowWhatsApp(true); }}>
              <i className="fab fa-whatsapp me-2"></i>Envoyer WhatsApp
            </button>
            <button type="button" className="btn btn-secondary" onClick={() => setShowDetails(false)}>Fermer</button>
          </>}>
          <div className="row">
            {[
              ['Nom', selected.participant?.name],
              ['Téléphone', formatPhone(selected.participant?.phone)],
              ['PDV', selected.pdv?.name],
              ['Hôtesse', selected.hotesse?.name],
              ['Session', selected.session?.name],
              ['Mode de jeu', MODE_LABELS[selected.play_mode] || selected.play_mode],
              ["Date d'enregistrement", new Date(selected.created_at).toLocaleString('fr-FR')],
              ['Nombre de bouteilles', `${selected.bottles_count}${selected.detail?.bouteilles ? ` (${Object.entries(selected.detail.bouteilles).map(([k, v]) => `${v} × ${k}`).join(', ')})` : ''}`],
              ...(selected.ticket_ref ? [['N° du ticket', selected.ticket_ref]] : []),
              ...(selected.played_at ? [['Date du jeu', new Date(selected.played_at).toLocaleString('fr-FR')]] : []),
            ].map(([k, v]) => (
              <div key={k} className="col-md-6 mb-3">
                <label className="form-label small mb-0" style={{ color: '#555' }}>{k}</label>
                <div className="fw-semibold" style={{ color: '#1a1a1a' }}>{v || '-'}</div>
              </div>
            ))}
            <div className="col-12 mb-3">
              <label className="form-label small" style={{ color: '#555' }}>Lots par lancer</label>
              <div className="d-flex flex-column gap-2">
                {selected.plays_history.map((h) => (
                  <div key={h.id} className="p-2 rounded" style={{ background: '#f4f6f9', border: '1px solid #dee2e6' }}>
                    <div className="d-flex flex-wrap align-items-center gap-2">
                      <span className="small" style={{ color: '#555' }}>Lancer {h.play_number}</span>
                      <span className="font-monospace small" style={{ color: '#1565c0' }}>{h.code}</span>
                      <StatusBadge status={h.status} />
                      <span className="fw-semibold" style={{ color: h.is_winner ? '#28a745' : '#999' }}>{h.prize_label || '-'}</span>
                      {h.delivered_at && <small className="text-muted">remis le {fmtDateTime(h.delivered_at)}</small>}
                      <button className="btn btn-link btn-sm p-0 ms-auto" onClick={() => setAuditId(auditId === h.id ? null : h.id)}>
                        {auditId === h.id ? 'Masquer' : 'Cases et journal'}
                      </button>
                    </div>
                    {auditId === h.id && <RoundAudit roundId={h.id} />}
                  </div>
                ))}
              </div>
            </div>
            {selected.ticket_photo_url && (
              <div className="col-12 mb-3">
                <label className="form-label fw-bold" style={{ color: '#e68a00' }}><i className="fas fa-receipt me-2"></i>Photo du ticket de caisse</label>
                <div className="p-3 rounded text-center" style={{ background: '#f4f6f9', border: '1px solid #dee2e6' }}>
                  <TicketPhoto url={selected.ticket_photo_url} />
                </div>
              </div>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
}
